import {
  snippetCompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
} from "@codemirror/autocomplete";
import { linter, type Diagnostic } from "@codemirror/lint";
import { EditorState, type Extension } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import { toast } from "sonner";
import { languageForPath, type LanguageId } from "@ide/shared";
import { activeEditor } from "@/workspace/active-editor";
import { activeWorkspace, findFileId, writeFile } from "@/workspace/file-ops";
import { runsAvailable } from "@/runs/local-runs";
import { openRunDialog } from "@/runs/runs-ui";
import { useWorkspace } from "@/workspace/store";
import { runInTerminal } from "@/workspace/terminal-panel";
import { activePluginPath } from "./active-path";
import { findSecrets } from "./secrets";
import type { PluginCommand } from "./registry";

/**
 * Plugins de linguagem e segurança: snippets no autocompletar, checagens que sublinham o código
 * (sem sair do navegador) e comandos que rodam no terminal individual da pessoa.
 */

const currentLanguage = (): LanguageId => languageForPath(activePluginPath());
const fileName = () => activePluginPath().split("/").pop() ?? "";

/** Fonte de snippets restrita a algumas linguagens (o arquivo aberto decide). */
function snippetSource(languages: LanguageId[], snippets: Completion[]) {
  return (ctx: CompletionContext): CompletionResult | null => {
    if (!languages.includes(currentLanguage())) return null;
    const word = ctx.matchBefore(/[\w.]+$/);
    if (!word || (word.from === word.to && !ctx.explicit)) return null;
    return { from: word.from, options: snippets, validFor: /^[\w.]*$/ };
  };
}

export const snippets = (languages: LanguageId[], list: Completion[]): Extension =>
  EditorState.languageData.of(() => [{ autocomplete: snippetSource(languages, list) }]);

export const snip = (label: string, detail: string, template: string) =>
  snippetCompletion(template, { label, detail, type: "snippet", boost: 50 });

/** Linter só para algumas linguagens (as outras não pagam o custo). */
function languageLinter(
  languages: LanguageId[],
  check: (view: EditorView) => Diagnostic[],
  source: string,
): Extension {
  return linter(
    (view) => (languages.includes(currentLanguage()) ? check(view).map((d) => ({ ...d, source })) : []),
    { delay: 600 },
  );
}

/** Uma linha por vez: (texto, início da linha, número da linha). */
function eachLine(view: EditorView, fn: (text: string, from: number, n: number) => void) {
  const doc = view.state.doc;
  for (let n = 1; n <= doc.lines; n++) {
    const line = doc.line(n);
    fn(line.text, line.from, n);
  }
}

async function runFileCommand(build: (path: string) => string | null, label: string) {
  const ws = activeWorkspace.current;
  const path = activePluginPath();
  if (!ws || !path) return void toast("Abra um arquivo primeiro.");
  const command = build(path);
  if (!command) return void toast(`Este comando é para arquivos ${label}.`);
  await runInTerminal(ws.session, command);
}

/** Escapa um caminho para o shell (aspas simples). */
const sh = (p: string) => `'${p.replace(/'/g, `'\\''`)}'`;

// ───────────── Java ─────────────

const JAVA_SNIPPETS = [
  snip("main", "public static void main", "public static void main(String[] args) {\n\t${}\n}"),
  snip("psvm", "public static void main", "public static void main(String[] args) {\n\t${}\n}"),
  snip("sout", "System.out.println", "System.out.println(${});"),
  snip("souf", "System.out.printf", 'System.out.printf("${%s}%n", ${valor});'),
  snip("fori", "for com índice", "for (int ${i} = 0; ${i} < ${n}; ${i}++) {\n\t${}\n}"),
  snip("foreach", "for each", "for (${var} ${item} : ${lista}) {\n\t${}\n}"),
  snip("trycatch", "try/catch", "try {\n\t${}\n} catch (${Exception} e) {\n\t${e.printStackTrace();}\n}"),
  snip("class", "classe pública", "public class ${Nome} {\n\n\tpublic ${Nome}() {\n\t\t${}\n\t}\n}"),
  snip("record", "record imutável", "public record ${Nome}(${String nome}) {}"),
  snip("interface", "interface", "public interface ${Nome} {\n\t${}\n}"),
  snip("enum", "enum", "public enum ${Nome} {\n\t${A}, ${B}\n}"),
  snip(
    "junit",
    "teste JUnit 5",
    "import org.junit.jupiter.api.Test;\nimport static org.junit.jupiter.api.Assertions.*;\n\nclass ${Nome}Test {\n\n\t@Test\n\tvoid ${deveFazerAlgo}() {\n\t\t${}\n\t\tassertEquals(${esperado}, ${atual});\n\t}\n}",
  ),
  snip(
    "springctrl",
    "controller REST (Spring Boot)",
    'import org.springframework.web.bind.annotation.*;\n\n@RestController\n@RequestMapping("/${recurso}")\npublic class ${Nome}Controller {\n\n\t@GetMapping\n\tpublic ${List<String>} listar() {\n\t\treturn ${List.of()};\n\t}\n\n\t@PostMapping\n\tpublic ${String} criar(@RequestBody ${String} corpo) {\n\t\treturn corpo;\n\t}\n}',
  ),
];

function javaChecks(view: EditorView): Diagnostic[] {
  const out: Diagnostic[] = [];
  const name = fileName().replace(/\.java$/, "");
  const text = view.state.doc.toString();
  const m =
    /\bpublic\s+(?:final\s+|abstract\s+|sealed\s+)*(class|interface|enum|record)\s+([A-Za-z_]\w*)/.exec(text);
  if (m && m[2] !== name) {
    const from = m.index + m[0].length - m[2]!.length;
    out.push({
      from,
      to: from + m[2]!.length,
      severity: "error",
      message: `A ${m[1]} pública "${m[2]}" precisa estar num arquivo chamado ${m[2]}.java (este é ${name}.java).`,
    });
  }
  eachLine(view, (line, from) => {
    const eq = /(\w+)\s*==\s*"[^"]*"|"[^"]*"\s*==\s*\w+/.exec(line);
    if (eq && !line.trim().startsWith("//")) {
      out.push({
        from: from + eq.index,
        to: from + eq.index + eq[0].length,
        severity: "warning",
        message: "Comparação de String com == compara referências. Use .equals().",
      });
    }
  });
  return out;
}

/** Nome qualificado da classe (pacote + classe) a partir do arquivo aberto. */
function javaMainClass(path: string, text: string): string {
  const cls = path
    .split("/")
    .pop()!
    .replace(/\.java$/, "");
  const pkg = /^\s*package\s+([\w.]+)\s*;/m.exec(text)?.[1];
  return pkg ? `${pkg}.${cls}` : cls;
}

export const JAVA_PLUGIN = {
  editor: (): Extension => [snippets(["java"], JAVA_SNIPPETS), languageLinter(["java"], javaChecks, "java")],
  commands: [
    {
      id: "java.run",
      title: "Java: compilar e executar a classe aberta",
      run: () =>
        runFileCommand((path) => {
          if (!path.endsWith(".java")) return null;
          const text = activeEditorText();
          const root = path.includes("src/") ? path.slice(0, path.indexOf("src/") + 3) : ".";
          return `mkdir -p out && javac -d out $(find ${sh(root)} -name '*.java') && java -cp out ${javaMainClass(path, text)}`;
        }, "Java (.java)"),
    },
    {
      id: "java.clean",
      title: "Java: apagar classes compiladas (out/)",
      run: () => {
        const ws = activeWorkspace.current;
        if (ws) void runInTerminal(ws.session, "rm -rf out && echo 'out/ limpo'");
      },
    },
  ] satisfies PluginCommand[],
};

const activeEditorText = () => activeEditor.current?.view.state.doc.toString() ?? "";

// ───────────── Python ─────────────

const PYTHON_SNIPPETS = [
  snip("def", "função", "def ${nome}(${args}) -> ${None}:\n\t${pass}"),
  snip("class", "classe", "class ${Nome}:\n\tdef __init__(self${, args}) -> None:\n\t\t${pass}"),
  snip(
    "dataclass",
    "@dataclass",
    "from dataclasses import dataclass\n\n\n@dataclass\nclass ${Nome}:\n\t${campo}: ${str}",
  ),
  snip(
    "main",
    'if __name__ == "__main__"',
    'def main() -> None:\n\t${pass}\n\n\nif __name__ == "__main__":\n\tmain()',
  ),
  snip(
    "with",
    "with open",
    'with open(${"arquivo.txt"}, encoding="utf-8") as ${f}:\n\t${conteudo = f.read()}',
  ),
  snip("try", "try/except", "try:\n\t${pass}\nexcept ${Exception} as e:\n\t${raise}"),
  snip(
    "fastapi",
    "rota FastAPI",
    'from fastapi import FastAPI\n\napp = FastAPI()\n\n\n@app.get("/${rota}")\ndef ${listar}() -> dict:\n\treturn {"ok": True}',
  ),
  snip(
    "pytest",
    "teste pytest",
    "def test_${comportamento}() -> None:\n\t${resultado} = ${funcao()}\n\tassert ${resultado} == ${esperado}",
  ),
  snip(
    "logger",
    "logging configurado",
    'import logging\n\nlogging.basicConfig(level=logging.INFO, format="%(asctime)s %(levelname)s %(name)s: %(message)s")\nlog = logging.getLogger(__name__)',
  ),
];

function pythonChecks(view: EditorView): Diagnostic[] {
  const out: Diagnostic[] = [];
  eachLine(view, (line, from) => {
    const trimmed = line.trimStart();
    if (trimmed.startsWith("#")) return;
    const indent = line.length - trimmed.length;
    if (line.slice(0, indent).includes("\t"))
      out.push({
        from,
        to: from + indent,
        severity: "warning",
        message: "PEP 8: use 4 espaços na indentação, não tabs.",
      });
    if (line.length > 99)
      out.push({
        from: from + 99,
        to: from + line.length,
        severity: "info",
        message: `PEP 8: linha com ${line.length} caracteres (recomendado até 99).`,
      });
    const none = /[=!]=\s*None\b/.exec(line);
    if (none)
      out.push({
        from: from + none.index,
        to: from + none.index + none[0].length,
        severity: "warning",
        message: `Compare com None usando "is" / "is not" (PEP 8).`,
      });
    if (/^\s*except\s*:/.test(line))
      out.push({
        from,
        to: from + line.length,
        severity: "warning",
        message: "except sem tipo captura até Ctrl+C. Use except Exception (ou o erro esperado).",
      });
    if (/^\s*from\s+\S+\s+import\s+\*/.test(line))
      out.push({
        from,
        to: from + line.length,
        severity: "info",
        message: "import * esconde de onde vêm os nomes. Importe só o que usa.",
      });
  });
  return out;
}

export const PYTHON_PLUGIN = {
  editor: (): Extension => [
    snippets(["python"], PYTHON_SNIPPETS),
    languageLinter(["python"], pythonChecks, "pep8"),
  ],
  commands: [
    {
      id: "python.run",
      title: "Python: rodar o arquivo aberto",
      run: () =>
        runFileCommand((path) => (path.endsWith(".py") ? `python3 ${sh(path)}` : null), "Python (.py)"),
    },
    {
      id: "python.pytest",
      title: "Python: rodar os testes (pytest)",
      run: () => {
        const ws = activeWorkspace.current;
        if (ws)
          void runInTerminal(
            ws.session,
            "python3 -m pytest -q || pip install pytest && python3 -m pytest -q",
          );
      },
    },
    {
      id: "python.venv",
      title: "Python: criar ambiente virtual e instalar dependências",
      run: () => {
        const ws = activeWorkspace.current;
        if (ws)
          void runInTerminal(
            ws.session,
            "python3 -m venv .venv && . .venv/bin/activate && (test -f requirements.txt && pip install -r requirements.txt || true)",
          );
      },
    },
  ] satisfies PluginCommand[],
};

// ───────────── Docker ─────────────

const DOCKER_SNIPPETS = [
  snip("from", "FROM com versão fixa", "FROM ${node}:${22-alpine}"),
  snip("nonroot", "usuário sem root", "RUN addgroup -S app && adduser -S app -G app\nUSER app"),
  snip(
    "aptinstall",
    "apt-get sem cache",
    "RUN apt-get update \\\n && apt-get install -y --no-install-recommends ${pacote} \\\n && rm -rf /var/lib/apt/lists/*",
  ),
  snip(
    "healthcheck",
    "HEALTHCHECK",
    "HEALTHCHECK --interval=30s --timeout=3s CMD ${wget -qO- http://localhost:3000/health || exit 1}",
  ),
  snip(
    "multistage",
    "build em dois estágios",
    'FROM ${node:22-alpine} AS build\nWORKDIR /app\nCOPY . .\nRUN ${npm ci && npm run build}\n\nFROM ${node:22-alpine}\nWORKDIR /app\nCOPY --from=build /app/${dist} ./${dist}\nUSER node\nCMD [${"node", "dist/index.js"}]',
  ),
];

function dockerChecks(view: EditorView): Diagnostic[] {
  const out: Diagnostic[] = [];
  let lastFrom: { from: number; to: number } | null = null;
  let hasUser = false;
  eachLine(view, (line, from) => {
    const t = line.trim();
    if (!t || t.startsWith("#")) return;
    const fromM = /^FROM\s+(?:--platform=\S+\s+)?(\S+)/i.exec(t);
    if (fromM) {
      const image = fromM[1]!;
      lastFrom = { from, to: from + line.length };
      hasUser = false;
      if (
        image.toLowerCase() !== "scratch" &&
        !image.includes("@") &&
        (!/:[^/]+$/.test(image) || /:latest$/i.test(image))
      )
        out.push({
          from,
          to: from + line.length,
          severity: "warning",
          message: `Fixe a versão da imagem (ex.: ${image.split(":")[0]}:22-alpine): "latest" muda sem aviso e quebra builds.`,
        });
    }
    if (/^USER\s+/i.test(t)) hasUser = !/^USER\s+(root|0)(\s|$)/i.test(t);
    if (/^ADD\s+(?!https?:)/i.test(t) && !/\.(tar|tgz|tar\.gz)\b/.test(t))
      out.push({
        from,
        to: from + line.length,
        severity: "info",
        message: "Prefira COPY a ADD para arquivos locais (ADD extrai e baixa coisas sozinho).",
      });
    if (/apt-get\s+install/.test(t) && !/--no-install-recommends/.test(t))
      out.push({
        from,
        to: from + line.length,
        severity: "info",
        message:
          "Use --no-install-recommends e apague /var/lib/apt/lists/* no mesmo RUN: a imagem fica menor.",
      });
    if (/^ENV\s+\S*(PASSWORD|SECRET|TOKEN|API_?KEY|PRIVATE_?KEY)\S*[\s=]/i.test(t))
      out.push({
        from,
        to: from + line.length,
        severity: "warning",
        message:
          "Segredo em ENV fica gravado na imagem para sempre. Passe em tempo de execução (variável de ambiente ou secret).",
      });
    if (/curl[^|]*\|\s*(ba)?sh/.test(t))
      out.push({
        from,
        to: from + line.length,
        severity: "warning",
        message:
          "curl | sh executa código remoto sem verificação. Baixe, confira o checksum e só então execute.",
      });
    if (/^RUN\s+cd\s/i.test(t))
      out.push({
        from,
        to: from + line.length,
        severity: "info",
        message: "Use WORKDIR em vez de RUN cd (o cd não vale para as próximas instruções).",
      });
  });
  const stage = lastFrom as { from: number; to: number } | null;
  if (stage && !hasUser)
    out.push({
      ...stage,
      severity: "warning",
      message: "O contêiner final roda como root. Crie um usuário e use USER (snippet: nonroot).",
    });
  return out;
}

const DOCKERFILES: Record<string, { dockerfile: string; compose: string }> = {
  node: {
    dockerfile: `# Gerado pelo plugin Docker do Kompil. Ajuste à sua aplicação.
FROM node:22-alpine AS deps
WORKDIR /app
COPY package*.json ./
RUN npm ci --omit=dev

FROM node:22-alpine
ENV NODE_ENV=production
WORKDIR /app
COPY --from=deps /app/node_modules ./node_modules
COPY . .
USER node
EXPOSE 3000
HEALTHCHECK --interval=30s --timeout=3s CMD wget -qO- http://localhost:3000/ || exit 1
CMD ["npm", "start"]
`,
    compose: `services:
  app:
    build: .
    ports:
      - "3000:3000"
    environment:
      NODE_ENV: production
    restart: unless-stopped
`,
  },
  python: {
    dockerfile: `# Gerado pelo plugin Docker do Kompil. Ajuste à sua aplicação.
FROM python:3.12-slim
ENV PYTHONDONTWRITEBYTECODE=1 PYTHONUNBUFFERED=1
WORKDIR /app
COPY requirements.txt ./
RUN pip install --no-cache-dir -r requirements.txt
COPY . .
RUN useradd --create-home app
USER app
EXPOSE 8000
CMD ["python", "main.py"]
`,
    compose: `services:
  app:
    build: .
    ports:
      - "8000:8000"
    restart: unless-stopped
`,
  },
  java: {
    dockerfile: `# Gerado pelo plugin Docker do Kompil. Ajuste à sua aplicação.
FROM eclipse-temurin:17-jdk-alpine AS build
WORKDIR /app
COPY src ./src
RUN mkdir -p out && javac -d out $(find src -name '*.java')

FROM eclipse-temurin:17-jre-alpine
WORKDIR /app
COPY --from=build /app/out ./out
RUN addgroup -S app && adduser -S app -G app
USER app
CMD ["java", "-cp", "out", "Main"]
`,
    compose: `services:
  app:
    build: .
    restart: unless-stopped
`,
  },
};

const DOCKERIGNORE = `node_modules
.venv
__pycache__
out
dist
.git
.env
*.log
`;

async function generate(files: Array<[string, string]>) {
  const ws = activeWorkspace.current;
  if (!ws) return void toast("Abra um projeto primeiro.");
  try {
    const created: string[] = [];
    for (const [path, content] of files) {
      if (findFileId(ws.session, path)) continue;
      await writeFile(ws.session, path, content);
      created.push(path);
    }
    if (!created.length) return void toast("Os arquivos já existem no projeto.");
    toast.success(`Criado: ${created.join(", ")}`);
    const id = findFileId(ws.session, created[0]!);
    if (id) useWorkspace.getState().openFile(id);
  } catch (err) {
    toast.error(err instanceof Error ? err.message : "Não foi possível criar os arquivos.");
  }
}

export const DOCKER_PLUGIN = {
  editor: (): Extension => [
    snippets(["dockerfile"], DOCKER_SNIPPETS),
    languageLinter(["dockerfile"], dockerChecks, "docker"),
  ],
  commands: [
    {
      id: "docker.dockerfile",
      title: "Docker: gerar Dockerfile e .dockerignore para este projeto",
      run: () => {
        const runtime = activeWorkspace.current?.detail.project.runtime ?? "node";
        return generate([
          ["Dockerfile", DOCKERFILES[runtime]!.dockerfile],
          [".dockerignore", DOCKERIGNORE],
        ]);
      },
    },
    {
      id: "docker.run-local",
      title: "Docker: rodar na sua máquina",
      run: () => {
        const ws = activeWorkspace.current;
        if (!ws) return void toast("Abra um projeto primeiro.");
        if (!runsAvailable) return void toast("Rodar contêineres é do Kompil Desktop (baixe o app).");
        openRunDialog({ id: ws.detail.project.id, name: ws.detail.project.name });
      },
    },
    {
      id: "docker.compose",
      title: "Docker: gerar docker-compose.yml",
      run: () => {
        const runtime = activeWorkspace.current?.detail.project.runtime ?? "node";
        return generate([["docker-compose.yml", DOCKERFILES[runtime]!.compose]]);
      },
    },
  ] satisfies PluginCommand[],
};

// ───────────── Detector de segredos ─────────────

export const secretScanner: Extension = linter(
  (view) => {
    const name = fileName();
    // .env.example é justamente o lugar dos valores de exemplo.
    if (/\.env\.(example|sample|template)$/.test(name)) return [];
    const text = view.state.doc.toString();
    if (text.length > 500_000) return [];
    const isEnv = name === ".env" || name.startsWith(".env.");
    return findSecrets(text).map(({ from, to, rule }) => ({
      from,
      to,
      severity: isEnv ? "info" : rule.severity,
      source: "segredos",
      message: isEnv
        ? `Possível ${rule.name}: este .env é compartilhado com quem participa do projeto. Use os segredos do ambiente de deploy.`
        : `Possível ${rule.name} no código. Tudo que está aqui é visto pelo time e entra no histórico: mova para uma variável de ambiente e gere uma credencial nova.`,
    }));
  },
  { delay: 700 },
);

// ───────────── Snippets populares ─────────────

const JS_LANGS: LanguageId[] = ["javascript", "jsx", "typescript", "tsx"];

const POPULAR_JS = [
  snip(
    "rfc",
    "componente React",
    "export function ${Componente}(${props}) {\n\treturn (\n\t\t<div>${}</div>\n\t);\n}",
  ),
  snip("us", "useState", "const [${valor}, set${Valor}] = useState(${inicial});"),
  snip("ue", "useEffect", "useEffect(() => {\n\t${}\n\treturn () => {${}};\n}, [${deps}]);"),
  snip(
    "exapp",
    "app Express",
    'import express from "express";\n\nconst app = express();\napp.use(express.json());\n\napp.get("/health", (_req, res) => res.json({ ok: true }));\n\napp.listen(${3000}, () => console.log("ouvindo na porta ${3000}"));',
  ),
  snip(
    "exroute",
    "rota Express",
    'router.${get}("/${recurso}", async (req, res, next) => {\n\ttry {\n\t\t${}\n\t\tres.json(${resultado});\n\t} catch (err) {\n\t\tnext(err);\n\t}\n});',
  ),
  snip(
    "nestctrl",
    "controller NestJS",
    'import { Body, Controller, Get, Post } from "@nestjs/common";\n\n@Controller("${recurso}")\nexport class ${Nome}Controller {\n\t@Get()\n\tlistar() {\n\t\treturn [];\n\t}\n\n\t@Post()\n\tcriar(@Body() corpo: ${unknown}) {\n\t\treturn corpo;\n\t}\n}',
  ),
  snip(
    "fetchj",
    "fetch com erro tratado",
    "const res = await fetch(${url});\nif (!res.ok) throw new Error(`HTTP \\${res.status}`);\nconst ${dados} = await res.json();",
  ),
  snip(
    "vtest",
    "teste Vitest",
    'import { describe, expect, it } from "vitest";\n\ndescribe("${assunto}", () => {\n\tit("${faz algo}", () => {\n\t\texpect(${atual}).toBe(${esperado});\n\t});\n});',
  ),
  snip("asyncfn", "função async", "async function ${nome}(${args}) {\n\t${}\n}"),
  snip("tryc", "try/catch", "try {\n\t${}\n} catch (err) {\n\t${console.error(err);}\n}"),
];

const POPULAR_SQL = [
  snip(
    "sqlsel",
    "SELECT com filtro",
    "SELECT ${colunas}\n  FROM ${tabela}\n WHERE ${condicao}\n ORDER BY ${coluna}\n LIMIT ${50};",
  ),
  snip("sqlins", "INSERT", "INSERT INTO ${tabela} (${colunas})\nVALUES (${valores});"),
  snip(
    "sqlupd",
    "UPDATE com WHERE",
    "UPDATE ${tabela}\n   SET ${coluna} = ${valor}\n WHERE ${id} = ${valor};",
  ),
  snip(
    "sqljoin",
    "JOIN",
    "SELECT ${a}.*, ${b}.${coluna}\n  FROM ${tabela_a} ${a}\n  JOIN ${tabela_b} ${b} ON ${b}.${a}_id = ${a}.id;",
  ),
  snip(
    "sqlcreate",
    "CREATE TABLE (MySQL)",
    "CREATE TABLE ${tabela} (\n  id BIGINT NOT NULL AUTO_INCREMENT PRIMARY KEY,\n  ${nome} VARCHAR(120) NOT NULL,\n  created_at DATETIME(3) NOT NULL DEFAULT CURRENT_TIMESTAMP(3)\n) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4;",
  ),
];

const POPULAR_GO = [
  snip("iferr", "if err != nil", "if err != nil {\n\treturn ${err}\n}"),
  snip("gomain", "package main", 'package main\n\nimport "fmt"\n\nfunc main() {\n\tfmt.Println("${olá}")\n}'),
  snip("handler", "http.HandlerFunc", "func ${nome}(w http.ResponseWriter, r *http.Request) {\n\t${}\n}"),
];

export const popularSnippets = (): Extension => [
  snippets(JS_LANGS, POPULAR_JS),
  snippets(["sql"], POPULAR_SQL),
  snippets(["go"], POPULAR_GO),
];
